import React from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Plus, Trash2, Code } from 'lucide-react'
import type { ScriptDef } from '../../services/skills'

const MAX_SCRIPT_SIZE = 51200 // 50 KB

const LANGUAGE_OPTIONS = [
  { value: 'python', label: 'Python' },
  { value: 'bash', label: 'Bash' },
  { value: 'node', label: 'Node.js' },
]

interface SkillScriptEditorProps {
  scripts: ScriptDef[]
  onChange: (scripts: ScriptDef[]) => void
  maxScripts?: number
}

export const SkillScriptEditor: React.FC<SkillScriptEditorProps> = ({
  scripts,
  onChange,
  maxScripts = 5,
}) => {
  const addScript = () => {
    if (scripts.length >= maxScripts) return
    onChange([...scripts, { name: '', description: '', language: 'python', code: '' }])
  }

  const updateScript = (index: number, field: keyof ScriptDef, value: string) => {
    const updated = scripts.map((s, i) => i === index ? { ...s, [field]: value } : s)
    onChange(updated)
  }

  const removeScript = (index: number) => {
    onChange(scripts.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Scripts run in a sandboxed container. Max {maxScripts} scripts, 50KB each.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addScript}
          disabled={scripts.length >= maxScripts}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Script
        </Button>
      </div>

      {scripts.length === 0 && (
        <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl">
          <Code className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No scripts yet. Add one to get started.</p>
        </div>
      )}

      {scripts.map((script, index) => {
        const codeSize = new Blob([script.code]).size
        const overLimit = codeSize > MAX_SCRIPT_SIZE
        return (
          <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Script {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeScript(index)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Script name"
                value={script.name}
                onChange={(e) => updateScript(index, 'name', e.target.value)}
              />
              <select
                className="h-10 px-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer"
                value={script.language}
                onChange={(e) => updateScript(index, 'language', e.target.value)}
              >
                {LANGUAGE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <Input
              placeholder="Description (what this script does)"
              value={script.description}
              onChange={(e) => updateScript(index, 'description', e.target.value)}
            />

            <div>
              <textarea
                className={`w-full min-h-[200px] px-3.5 py-2.5 text-sm font-mono border rounded-xl bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-400 ${
                  overLimit ? 'border-red-300' : 'border-gray-200 dark:border-gray-700'
                }`}
                placeholder={`# Write your ${script.language} code here...`}
                value={script.code}
                onChange={(e) => updateScript(index, 'code', e.target.value)}
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${overLimit ? 'text-red-500' : 'text-gray-400'}`}>
                  {(codeSize / 1024).toFixed(1)}KB / 50KB
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SkillScriptEditor
